import { PremiumCode } from '@app/domain/model/eshop/transport/PremiumCode';
import { PremiumType } from '@app/domain/model/eshop/transport/PremiumType';
import { ProductCode } from '@app/domain/model/eshop/transport/ProductCode';
import { ProductDataRider } from '@app/domain/model/eshop/transport/ProductDataRider';
import { RiderTypeCode } from '@app/domain/model/eshop/transport/RiderTypeCode';
import { TransportPremiumsResponse } from '@app/domain/model/eshop/transport/TransportPremiumsResponse';
import { PremiumsPaymentSchedule } from '@app/domain/model/eshop/transport/PremiumsPaymentSchedule';
import { logger } from '@app/infrastructure/logger/logger';
import { loadRiders } from '@app/service/eshop/transport/loaders/loadRiders';
import { getRiderIdentifier } from '@app/app/esales/transport/_sections/helpers/getRiderIdentifier';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';

export function usePriceCalculation(
    initialPremiums: TransportPremiumsResponse[],
    insuranceDuration: string,
    vehicleTypeCode: string,
    initialInsurancePackage?: string,
    initialSelectedRiderTypeCodes?: string[],
) {
    const [selectedMtplPremium, setSelectedMtplPremium] = useState<Nullable<TransportPremiumsResponse>>(null);
    const [selectedCascoPremium, setSelectedCascoPremium] = useState<Nullable<TransportPremiumsResponse>>(null);
    const [insurancePackage, setInsurancePackage] = useState<string>(initialInsurancePackage ?? '');
    const [selectedRiderTypeCodes, setSelectedRiderTypeCodes] = useState<string[]>(initialSelectedRiderTypeCodes ?? []);
    const [availableRiders, setAvailableRiders] = useState<ProductDataRider[]>([]);
    const [isLoadingRiders, setIsLoadingRiders] = useState(false);

    const loadAvailableRiders = useCallback(async () => {
        if (!insuranceDuration) return;

        setIsLoadingRiders(true);
        try {
            let allRiders: ProductDataRider[] = [];

            if (selectedCascoPremium && selectedMtplPremium) {
                const [cascoRiders, mtplRiders] = await Promise.all([
                    loadRiders(
                        ProductCode.Casco,
                        vehicleTypeCode,
                        insuranceDuration,
                        selectedCascoPremium.riskPackageCode,
                    ),
                    loadRiders(
                        ProductCode.EcaPap,
                        vehicleTypeCode,
                        insuranceDuration,
                        selectedMtplPremium.riskPackageCode,
                    ),
                ]);

                const cascoMtplRider = mtplRiders.find(
                    rider => getRiderIdentifier(rider) === RiderTypeCode.CascoMtplRider,
                );

                allRiders = cascoMtplRider ? [...cascoRiders, cascoMtplRider] : cascoRiders;
            } else if (selectedCascoPremium) {
                const cascoRiders = await loadRiders(
                    ProductCode.Casco,
                    vehicleTypeCode,
                    insuranceDuration,
                    selectedCascoPremium.riskPackageCode,
                );
                allRiders = cascoRiders;
            } else if (selectedMtplPremium && insurancePackage === PremiumType.Mtpl) {
                const mtplRiders = await loadRiders(
                    ProductCode.EcaPap,
                    vehicleTypeCode,
                    insuranceDuration,
                    selectedMtplPremium.riskPackageCode,
                );
                allRiders = mtplRiders;
            }

            setAvailableRiders(allRiders);
        } catch (error) {
            logger.logException(error);
            setAvailableRiders([]);
        } finally {
            setIsLoadingRiders(false);
        }
    }, [selectedCascoPremium, selectedMtplPremium, insuranceDuration, vehicleTypeCode, insurancePackage]);

    const loadAvailableRidersWithParams = useCallback(
        async (
            cascoPremium: Nullable<TransportPremiumsResponse>,
            mtplPremium: Nullable<TransportPremiumsResponse>,
            packageType: string,
            duration: string,
        ) => {
            if (!duration) return;

            setIsLoadingRiders(true);
            try {
                let allRiders: ProductDataRider[] = [];

                if (cascoPremium && mtplPremium) {
                    const [cascoRiders, mtplRiders] = await Promise.all([
                        loadRiders(ProductCode.Casco, vehicleTypeCode, duration, cascoPremium.riskPackageCode),
                        loadRiders(ProductCode.EcaPap, vehicleTypeCode, duration, mtplPremium.riskPackageCode),
                    ]);

                    const cascoMtplRider = mtplRiders.find(
                        rider => getRiderIdentifier(rider) === RiderTypeCode.CascoMtplRider,
                    );

                    allRiders = cascoMtplRider ? [...cascoRiders, cascoMtplRider] : cascoRiders;
                } else if (cascoPremium) {
                    const cascoRiders = await loadRiders(
                        ProductCode.Casco,
                        vehicleTypeCode,
                        duration,
                        cascoPremium.riskPackageCode,
                    );
                    allRiders = cascoRiders;
                } else if (mtplPremium && packageType === PremiumType.Mtpl) {
                    const mtplRiders = await loadRiders(
                        ProductCode.EcaPap,
                        vehicleTypeCode,
                        duration,
                        mtplPremium.riskPackageCode,
                    );
                    allRiders = mtplRiders;
                }

                setAvailableRiders(allRiders);
            } catch (error) {
                logger.logException(error);
                setAvailableRiders([]);
            } finally {
                setIsLoadingRiders(false);
            }
        },
        [vehicleTypeCode],
    );

    const selectedRiders = useMemo(() => {
        return availableRiders.filter(rider => selectedRiderTypeCodes.includes(getRiderIdentifier(rider)));
    }, [availableRiders, selectedRiderTypeCodes]);

    const totalPrice = useMemo(() => {
        let totalPrice = 0;

        if (selectedMtplPremium) {
            totalPrice +=
                selectedMtplPremium?.premiumAfterDiscount && selectedMtplPremium.premiumAfterDiscount > 0
                    ? selectedMtplPremium?.premiumAfterDiscount
                    : selectedMtplPremium?.premium || 0;
        }

        if (selectedCascoPremium) {
            totalPrice +=
                selectedCascoPremium?.premiumAfterDiscount && selectedCascoPremium.premiumAfterDiscount > 0
                    ? selectedCascoPremium?.premiumAfterDiscount
                    : selectedCascoPremium?.premium || 0;
        }

        if (selectedCascoPremium && selectedMtplPremium) {
            const cascoPrice =
                selectedCascoPremium?.premiumAfterDiscount && selectedCascoPremium.premiumAfterDiscount > 0
                    ? selectedCascoPremium?.premiumAfterDiscount
                    : selectedCascoPremium?.premium || 0;
            const mtplPrice =
                selectedMtplPremium?.premiumAfterDiscount && selectedMtplPremium.premiumAfterDiscount > 0
                    ? selectedMtplPremium?.premiumAfterDiscount
                    : selectedMtplPremium?.premium || 0;
            totalPrice = cascoPrice + mtplPrice;
        }
        const ridersPrice = selectedRiders.reduce((total, rider) => total + rider.premiums.premium, 0);

        return totalPrice + ridersPrice;
    }, [selectedCascoPremium, selectedMtplPremium, selectedRiders]);

    const priceToPayNow = useMemo(() => {
        const selectedPremiums: TransportPremiumsResponse[] = [];
        if (selectedMtplPremium) selectedPremiums.push(selectedMtplPremium);
        if (selectedCascoPremium) selectedPremiums.push(selectedCascoPremium);

        const allSchedules: PremiumsPaymentSchedule[] = [];
        selectedPremiums.forEach(premium => {
            if (premium.paymentSchedule && premium.paymentSchedule.length > 0) {
                allSchedules.push(...premium.paymentSchedule);
            }
        });

        const ridersPrice = selectedRiders.reduce((total, rider) => total + rider.premiums.premium, 0);

        if (allSchedules.length === 0) {
            return totalPrice;
        }

        const scheduleMap = new Map<string, { amount: number; amountAfterDiscount: number }>();
        allSchedules.forEach(schedule => {
            const current = scheduleMap.get(schedule.date) ?? { amount: 0, amountAfterDiscount: 0 };
            scheduleMap.set(schedule.date, {
                amount: current.amount + schedule.amount,
                amountAfterDiscount: current.amountAfterDiscount + schedule.amountAfterDiscount,
            });
        });

        const sortedSchedules = Array.from(scheduleMap.entries())
            .map(([date, { amount, amountAfterDiscount }]) => ({ date, amount, amountAfterDiscount }))
            .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

        const firstPaymentAmount = sortedSchedules.length > 0 ? sortedSchedules[0].amountAfterDiscount : 0;

        return firstPaymentAmount + ridersPrice;
    }, [selectedCascoPremium, selectedMtplPremium, totalPrice, selectedRiders]);

    const handleInsuranceChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const insuranceCode = e.target.value;
            setInsurancePackage(insuranceCode);

            if (insuranceCode === PremiumType.Mtpl) {
                setSelectedMtplPremium(
                    initialPremiums.find(p => p.riskPackageCode === PremiumCode.Mtpl && !p.withCasco) ?? null,
                );
            }

            if (insuranceCode === PremiumType.CascoMtpl) {
                setSelectedMtplPremium(
                    initialPremiums.find(p => p.riskPackageCode === PremiumCode.Mtpl && p.withCasco) ?? null,
                );
            }

            if (insuranceCode === PremiumType.Casco) {
                setSelectedMtplPremium(null);
            }

            setSelectedCascoPremium(null);
            setAvailableRiders([]);
            setSelectedRiderTypeCodes([]);
        },
        [initialPremiums],
    );

    const handleCascoPremiumChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const premiumCode = e.target.value;
            const premium = initialPremiums.find(p => p.code === premiumCode) ?? null;
            setSelectedCascoPremium(premium);
            setAvailableRiders([]);
            setSelectedRiderTypeCodes([]);
        },
        [initialPremiums],
    );

    const handleRidersChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const { value, checked } = e.target;
            const riderIdentifier = value;

            const currentRider = availableRiders.find(rider => getRiderIdentifier(rider) === riderIdentifier);

            if (!currentRider) return;

            setSelectedRiderTypeCodes(prev => {
                if (checked) {
                    if (
                        currentRider.typeCode === RiderTypeCode.HelpOnRoadPlus ||
                        currentRider.typeCode === RiderTypeCode.ExtendedHelpOnRoad
                    ) {
                        const conflictingIdentifier = availableRiders
                            .filter(
                                rider =>
                                    (rider.typeCode === RiderTypeCode.HelpOnRoadPlus ||
                                        rider.typeCode === RiderTypeCode.ExtendedHelpOnRoad) &&
                                    getRiderIdentifier(rider) !== riderIdentifier,
                            )
                            .find(rider => prev.includes(getRiderIdentifier(rider)));

                        const updatedTypeCodes = conflictingIdentifier
                            ? prev.filter(code => code !== getRiderIdentifier(conflictingIdentifier))
                            : prev;

                        return [...updatedTypeCodes, riderIdentifier];
                    }

                    return [...prev, riderIdentifier];
                } else {
                    return prev.filter(code => code !== riderIdentifier);
                }
            });
        },
        [availableRiders],
    );

    const updateSelectedPremiums = useCallback(() => {
        if (selectedMtplPremium) {
            const updatedMtplPremium = initialPremiums.find(
                premium =>
                    premium.riskPackageCode === selectedMtplPremium.riskPackageCode &&
                    premium.withCasco === selectedMtplPremium.withCasco,
            );

            if (updatedMtplPremium && updatedMtplPremium.code !== selectedMtplPremium.code) {
                setSelectedMtplPremium(updatedMtplPremium);
            }
        }

        if (selectedCascoPremium) {
            const updatedCascoPremium = initialPremiums.find(
                premium => premium.riskPackageCode === selectedCascoPremium.riskPackageCode,
            );

            if (updatedCascoPremium && updatedCascoPremium.code !== selectedCascoPremium.code) {
                setSelectedCascoPremium(updatedCascoPremium);
            }
        }
    }, [initialPremiums, selectedMtplPremium, selectedCascoPremium]);

    const initializeFromSession = useCallback(
        (
            packageType: string,
            mtplPackageCode: Nullable<string>,
            cascoPackageCode: Nullable<string>,
            riderTypeCodes: string[],
        ) => {
            setInsurancePackage(packageType);

            let loadedMtplPremium: Nullable<TransportPremiumsResponse> = null;
            let loadedCascoPremium: Nullable<TransportPremiumsResponse> = null;

            if (mtplPackageCode) {
                const mtplPremium = initialPremiums.find(premium => premium.riskPackageCode === mtplPackageCode);
                if (mtplPremium) {
                    setSelectedMtplPremium(mtplPremium);
                    loadedMtplPremium = mtplPremium;
                }
            }

            if (cascoPackageCode) {
                const cascoPremium = initialPremiums.find(premium => premium.riskPackageCode === cascoPackageCode);
                if (cascoPremium) {
                    setSelectedCascoPremium(cascoPremium);
                    loadedCascoPremium = cascoPremium;
                }
            }

            if (riderTypeCodes && riderTypeCodes.length > 0) {
                setSelectedRiderTypeCodes(riderTypeCodes);
            }

            return { loadedMtplPremium, loadedCascoPremium };
        },
        [initialPremiums],
    );

    return {
        priceState: {
            selectedCascoPremium,
            selectedMtplPremium,
            availableRiders,
            selectedRiders,
            totalPrice,
            priceToPayNow,
            isLoadingRiders,
        },
        insurancePackage,
        handleInsuranceChange,
        handleCascoPremiumChange,
        handleRidersChange,
        loadAvailableRiders,
        loadAvailableRidersWithParams,
        updateSelectedPremiums,
        initializeFromSession,
    };
}
